import { FC, useEffect, useState } from "react";
import "./style.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { PATH_LIST } from "shared/lib/react-router";
import { sircapApi } from "shared/api/sircap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useStore } from "zustand";
import { ContentType } from "shared/api/http-client";
import { blobToFile } from "helpers/functions";
import { DocumentType } from "static/types";
import { accountStore } from "entities/account";
import moment from "moment";
import PersonalDocumentModal from "../components/personal-document-modal";
import TakePhotoModal from "../components/take-photo-modal";
import SubmitPhotoModal from "../components/submit-photo-modal";
import DocumentTypeBtn from "../components/document-type-btn";
import { VerificationBtn } from "../components/button";
import FormField from "shared/ui/form-field";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { BioLoadingSpinner } from "widgets/loading";
import { buildDocumentInfo } from "./document-info";

type PropType = {};

enum ModalStep {
  Zero,
  Start,
  PhotoPassport,
  SubmitPassport,
  PhotoIdFront,
  SubmitIdFront,
  PhotoIdBack,
  SubmitIdBack,
}

const TakePhotoModalType: Record<any, any> = {
  [DocumentType.Passport]: ModalStep.PhotoPassport,
  [DocumentType.IdCard]: ModalStep.PhotoIdFront,
  [DocumentType.IdCardBack]: ModalStep.PhotoIdBack,
};

const SubmitPhotoModalType: Record<any, any> = {
  [DocumentType.Passport]: ModalStep.SubmitPassport,
  [DocumentType.IdCard]: ModalStep.SubmitIdFront,
  [DocumentType.IdCardBack]: ModalStep.SubmitIdBack,
};

const formatDateInput = (raw: string): string => {
  const digits = raw.replace(/\D/g, "");
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4, 8)}`;
};

export const ChooseDocTypePage: FC<PropType> = () => {
  const [docType, setDocType] = useState<DocumentType>(DocumentType.Passport); // DocumentType.Passport
  const [step, setStep] = useState<ModalStep>(ModalStep.Zero);
  const [firstImage, setFirstImage] = useState<any>();
  const [idBackImage, setIdBackImage] = useState<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("onboarding");
  const { t: a } = useTranslation("app");
  const { t: oa } = useTranslation("onboarding-auth");
  const { t: err } = useTranslation("errors");
  const [isSubmit, setIsSubmit] = useState(false);
  const {
    synckyc,
    parsModalOpened,
    setParsModalOpened,
    parsedData,
    setParsedData,
    removeParsedData,
  } = useStore(accountStore);

  const [checkingDocument, setCheckingDocument] = useState(false);

  const country = location.state?.country;
  const citizenship = location.state?.citizenship;
  const type = location.state?.type;

  const isInternalPassport = type === "internalPassport";
  const needToCheck = !!citizenship && !isInternalPassport;

  // Auto-trigger internal passport flow when type is "internalPassport"
  useEffect(() => {
    if (isInternalPassport) {
      setDocType(DocumentType.Passport);
      setStep(1);
    }
  }, [isInternalPassport]);

  const ModalPassport = () => {
    setDocType(DocumentType.Passport);
    setStep(ModalStep.Start);
  };
  const ModalIdCard = () => {
    setDocType(DocumentType.IdCard);
    setStep(ModalStep.Start);
  };
  const ModalIdCardBack = () => {
    setDocType(DocumentType.IdCardBack);
    setStep(ModalStep.Start);
  };

  const SendDocument = async (
    docType: "PASSPORT" | "ID_CARD" = "PASSPORT",
    documentInfo: any = null,
    docSubType?: "FRONT_SIDE" | "BACK_SIDE"
  ) => {
    let imageName = isInternalPassport
      ? `INTERNAL_PASSPORT.jpg`
      : `PASSPORT.jpg`;
    if (docSubType) {
      if (docSubType === "FRONT_SIDE") {
        imageName = `ID_CARD-FRONT_SIDE.jpg`;
      } else if (docSubType === "BACK_SIDE") {
        imageName = `ID_CARD-BACK_SIDE.jpg`;
      }
    }
    const resp = await sircapApi.onboarding.document(
      {
        country: country,
        citizenship: citizenship,
        idDocType: isInternalPassport ? "INTERNAL_PASSPORT" : docType,
        ...(docSubType && { idDocSubType: docSubType }),
        fiz: blobToFile(
          docSubType && docSubType === "BACK_SIDE" ? idBackImage : firstImage,
          imageName
        ),
        ...(documentInfo && needToCheck && { document_info: documentInfo }),
      },
      { type: ContentType.FormData }
    );
    if (resp.error) {
      toast.error(err(String(resp.error.code)));
    } else {
      const kyc = resp.data.data;
      synckyc(kyc);
    }
  };

  const SubmitPassportPhoto = async () => {
    try {
      setIsSubmit(true);
      if (needToCheck) {
        await CheckDocument();
      } else {
        await SendDocument("PASSPORT");
        navigate(PATH_LIST.uploadCredentials);
      }
      setStep(ModalStep.Zero);
    } catch (error) {
      console.error("Error during passport submission:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmit(false);
    }
  };
  const SubmitIdFrontPhoto = async () => {
    setIsSubmit(true);

    try {
      setDocType(DocumentType.IdCardBack);
      setStep(ModalStep.PhotoIdBack);
    } catch (error) {
      console.error("Error during id card submission:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmit(false);
    }

    setIsSubmit(false);
  };
  const SubmitIdBackPhoto = async () => {
    if (needToCheck) {
      setStep(ModalStep.Zero);
      await CheckDocument();
    } else {
      setIsSubmit(true);
      await SendDocument("ID_CARD", null, "FRONT_SIDE");
      await SendDocument("ID_CARD", null, "BACK_SIDE");
      setStep(ModalStep.Zero);
      setIsSubmit(false);
      navigate(PATH_LIST.uploadCredentials);
    }
  };

  const CheckDocument = async () => {
    setCheckingDocument(true);

    const checkDocument = await sircapApi.onboarding.checkDocument(
      {
        type: "PARSING",
        fiz: blobToFile(firstImage, `PASSPORT.jpg`),
      },
      { type: ContentType.FormData }
    );

    // Распознавание — это удобство, а не условие прохождения верификации.
    // Раньше ветка выходила, не сбросив `checkingDocument`, и клиент навсегда
    // оставался на экране «проверяем документы». Теперь любой отказ ведёт на ту
    // же форму подтверждения, просто с пустыми полями: клиент заполняет их сам
    // и идёт дальше. Сообщать об этом не нужно — для него это обычный ввод.
    if (checkDocument.error) {
      setParsedData(null);
      setParsModalOpened(true);
      setStep(ModalStep.Zero);
      setCheckingDocument(false);
      setIsSubmit(false);
      return;
    }

    // if (
    //   checkDocument.data?.data?.is_good_quality === false ||
    //   checkDocument.data?.data?.result === "FAILED"
    // ) {
    //   console.log(step);
    //   toast.error("Id card verification failed — the photo may be unclear.");
    //   setIsSubmit(false);
    //   console.log(step);
    //   setParsedInfo(false);
    //   setCheckingDocument(false);
    //   return;
    // }

    // `?? null`: при `is_good_quality === false` бэкенд отдаёт 200 и `info`
    // отсутствует — стор ждёт именно null, а не undefined.
    setParsedData(checkDocument.data?.data?.data?.info ?? null);
    setParsModalOpened(true);
    setStep(ModalStep.Zero);
    setCheckingDocument(false);
  };

  const confirmParsedInfo = async (values: any) => {
    setIsSubmit(true);

    // Собирается ВСЕГДА, в том числе когда распознавание не сработало:
    // источник истины — форма. См. document-info.ts.
    const documentInfo = buildDocumentInfo(values, parsedData ?? null);

    if (docType === DocumentType.Passport) {
      await SendDocument("PASSPORT", documentInfo);
    } else {
      await SendDocument("ID_CARD", documentInfo, "FRONT_SIDE");
      await SendDocument("ID_CARD", documentInfo, "BACK_SIDE");
    }

    removeParsedData();
    setParsModalOpened(false);
    navigate(PATH_LIST.uploadCredentials);
    setIsSubmit(false);
  };

  // useEffect(() => {
  //   console.log("parsedData - " + JSON.stringify(parsedData));
  // }, [parsedData]);

  return (
    <>
      <div className="verification-choose-doc-type">
        <div className="choose-doc-type-main">
          <div className="verification-choose-doc-type-top">
            <p className="register-title">
              {parsModalOpened
                ? t("confirmDocumentDetails")
                : t("takeDocumentPhoto")}
            </p>
            <span className="register-subtitle">
              {parsModalOpened
                ? t("confirmDocumentDetailsSubtitle")
                : t("chooseDocumentType")}
            </span>
          </div>

          {parsModalOpened ? (
            <div className="verification-choose-doc-type-form">
              <Formik
                initialValues={{
                  first_name:
                    parsedData?.original_first_name ||
                    parsedData?.first_name ||
                    "",
                  last_name:
                    parsedData?.original_last_name ||
                    parsedData?.last_name ||
                    "",
                  document_number: parsedData?.document_number || "",
                  birth_date: parsedData?.birth_date
                    ? moment(parsedData.birth_date, "YYYY-MM-DD", true).isValid()
                      ? moment(parsedData.birth_date, "YYYY-MM-DD").format("DD.MM.YYYY")
                      : parsedData.birth_date
                    : "",
                }}
                onSubmit={(values) => confirmParsedInfo(values)}
              >
                {({ values, isValid }) => (
                  <Form>
                    <div className="building-apartment-wrapper">
                      <FormField
                        customClass="registration-field"
                        type="text"
                        id="first_name"
                        label={oa("firstName")}
                        placeholder={oa("firstName")}
                        disabled={isSubmit}
                      />
                      <FormField
                        customClass="registration-field"
                        type="text"
                        id="last_name"
                        label={oa("lastName")}
                        placeholder={oa("lastName")}
                        disabled={isSubmit}
                      />
                    </div>
                    <FormField
                      customClass="registration-field"
                      type="text"
                      id="document_number"
                      label={t("documentNumber")}
                      placeholder={t("documentNumber")}
                      disabled={isSubmit}
                    />
                    <div className="registration-field form-row">
                      <label htmlFor="birth_date">{t("dateOfBirth")}</label>
                      <Field name="birth_date">
                        {({ field, form }: any) => (
                          <input
                            {...field}
                            id="birth_date"
                            type="text"
                            inputMode="numeric"
                            placeholder="ДД.ММ.ГГГГ"
                            maxLength={10}
                            disabled={isSubmit}
                            onChange={(e) => {
                              form.setFieldValue(
                                "birth_date",
                                formatDateInput(e.target.value),
                              );
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="birth_date"
                        component="span"
                        className="error"
                      />
                    </div>
                    <VerificationBtn
                      type="submit"
                      disabled={
                        !isValid ||
                        isSubmit ||
                        // `String(v ?? "")`: распознавание возвращает не только
                        // строки, и голый `v.trim()` ронял рендер всей страницы
                        // на первом же нестроковом значении.
                        Object.values(values).some((v) => !String(v ?? "").trim())
                      }
                      text={a("continue")}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          ) : (
            <>
              <DocumentTypeBtn type={t("idCard")} onClick={ModalIdCard} />
              <DocumentTypeBtn type={t("passport")} onClick={ModalPassport} />
            </>
          )}
        </div>
      </div>

      {checkingDocument && (
        <BioLoadingSpinner
          className="checking-document-loader"
          text={t("checkingDocuments")}
        />
      )}

      {step > ModalStep.Zero && (
        <PersonalDocumentModal
          type={docType as any}
          onOpen={() => setStep(TakePhotoModalType[docType!])}
          onClose={() =>
            !isInternalPassport
              ? setStep(ModalStep.Zero)
              : navigate(PATH_LIST.uploadCredentials)
          }
        />
      )}

      {[ModalStep.PhotoPassport, ModalStep.PhotoIdFront].includes(step) && (
        <TakePhotoModal
          type={docType!}
          onOpen={(image: string) => {
            setFirstImage(image);
            setStep(SubmitPhotoModalType[docType!]);
          }}
          onClose={() => setStep(ModalStep.Start)}
        />
      )}

      {step === ModalStep.PhotoIdBack && (
        <TakePhotoModal
          type={docType!}
          onOpen={(image: string) => {
            setIdBackImage(image);
            setStep(SubmitPhotoModalType[docType!]);
          }}
          onClose={() => {
            setDocType(DocumentType.IdCard);
            setStep(ModalStep.SubmitIdFront);
          }}
        />
      )}

      {step === ModalStep.SubmitPassport && (
        <SubmitPhotoModal
          type={DocumentType.Passport}
          onSubmit={SubmitPassportPhoto}
          image={firstImage}
          onClose={() => setStep(TakePhotoModalType[docType!])}
          isSubmit={isSubmit}
        />
      )}

      {step === ModalStep.SubmitIdFront && (
        <SubmitPhotoModal
          type={DocumentType.IdCard}
          onSubmit={SubmitIdFrontPhoto}
          image={firstImage}
          onClose={() => setStep(TakePhotoModalType[docType!])}
          isSubmit={isSubmit}
        />
      )}

      {step === ModalStep.SubmitIdBack && (
        <SubmitPhotoModal
          type={DocumentType.IdCardBack}
          onSubmit={SubmitIdBackPhoto}
          image={idBackImage}
          onClose={() => setStep(TakePhotoModalType[docType!])}
          isSubmit={isSubmit}
        />
      )}
    </>
  );
};
